import { useNavigate } from 'react-router-dom'
import { Plus, X, LoaderCircle } from 'lucide-react'

import useQuoteTotals from '@/dealers/pages/Request/QuoteBuilder/useQuoteTotals'
import Summary from '@/dealers/pages/Request/Summary'
import Action from '@/gf/components/Action'
import AddAccountMachineModal from '@/gf/components/AddAccountMachineModal'
import Checkbox from '@/gf/components/Checkbox'
import Field from '@/gf/components/Field'
import Form from '@/gf/components/Form'
import PriceV2 from '@/gf/components/inputs/PriceV2'
import QuantityInput from '@/gf/components/inputs/Quantity'
import TextInput from '@/gf/components/inputs/Text'
import MoneyM from '@/gf/modules/Money'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/gf/components/ui/alert-dialog'
import { Maybe, Money } from '@/types'
import Box from '@/gf/components/Box'
import LocationModal from './LocationModal'
import MachineSelect from '../pages/CreateRequest/MachineSelect'
import VendorsSelector from '../pages/CreateExternalOrder/VendorsSelector'
import Upload from '../pages/CreateExternalOrder/Upload'
import useSession from '../hooks/useSession'
import useGqlClient from '../hooks/useGqlClient'
import {
  ExternalOrderError,
  useAllBranchesQuery,
  useExternalOrderLocationGroupsQuery,
  useLogExternalOrderMutation,
  useGetWorkOrderTasksQuery,
  useSelectedMachineQuery,
  useUpdateExternalOrderMutation,
} from '../_gen/gql'
import JobCodeCombobox from '../pages/Order/YourOrder/JobCodeCombobox'
import useToggle from '@/gf/hooks/useToggle'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useUpload from '../pages/CreateExternalOrder/useUpload'
import Address from '../modules/Address'
import LocationSelector, {
  Query as LocationsQuery,
} from '../pages/CreateExternalOrder/LocationSelector'
import AddVendorModal from './AddVendorModal'
import Modal from '@/gf/components/ModalNext'
import uniqueId from 'lodash/uniqueId'
import JobSelector from './JobSelector'
import useMsgs from '@/gf/hooks/useMsgs'
import NextPurchaseOrderNumber from './NextPurchaseOrderNumber'
import CostCodeSelector from './CostCodeSelector'
import BranchSelector from '../pages/CreateRequest/PartDetailsStep/BranchSelector'
import TextArea from '@/gf/components/TextArea'
import ImageViewer from './ImageViewer'
import WorkOrderCombobox from '../pages/CreateRequest/WorkOrderCombobox'

import FleetioWorkOrderNumber from '@/buyers/pages/CreateRequest/FleetioWorkOrderNumber'
import ClueWorkOrderNumber from '@/buyers/pages/CreateRequest/ClueWorkOrderNumber'
import ClueWorkOrderNumberInput from './ClueWorkOrderNumberInput'

import FleetioLocationSelector from '@/buyers/pages/Order/YourOrder/FleetioLocationSelector'

type JobQuantity = {
  id: string
  jobId: string
  quantity: number
}

type CostCode = {
  id: string | null
}

type PhaseCode = {
  id: string | null
}

type Item = {
  id: Maybe<string>
  partNumber: string
  description: string
  unitPrice: Money | null
  quantity: number | null
  jobQuantities: JobQuantity[]
  costCode: CostCode | null
  phaseCode: PhaseCode | null
  /** M5 job code (TaskId) for the line (GEA-9808). */
  taskNumber: string | null
}

type Fields = {
  vendorId: string | null
  locationId: string | null
  locationGroupId: string | null
  machineInvolved: boolean
  orgMachineId: string | null
  branchId: string | null
  purchaseOrderNumber: string
  workOrderNumber: string
  notes: string | null
  shippingCost: Money | null
  taxCost: Money | null
  documentUrl: string | null
  documentId: string | null
  items: Item[]
  fleetioWorkOrderId: number | null
  externalWorkOrderId: string | null
}

const zeroPrice = MoneyM.fromDecimal(0, 'USD')

const newItem = {
  id: null,
  partNumber: '',
  description: '',
  unitPrice: null,
  quantity: 1,
  jobQuantities: [],
  costCode: null,
  phaseCode: null,
  taskNumber: null,
}

const newFields = {
  vendorId: null,
  locationId: null,
  locationGroupId: null,
  machineInvolved: true,
  orgMachineId: null,
  branchId: null,
  purchaseOrderNumber: '',
  workOrderNumber: '',
  notes: null,
  shippingCost: null,
  taxCost: null,
  documentUrl: null,
  documentId: null,
  items: [newItem],
  fleetioWorkOrderId: null,
  externalWorkOrderId: null,
}

const ExternalOrderForm = ({
  storeOrderId,
  initialValues,
}: {
  storeOrderId?: string
  initialValues?: Fields
}) => {
  const navigate = useNavigate()
  const { featureFlags, features, organization, user, orgId } = useSession()
  const client = useGqlClient()
  const branches = useAllBranchesQuery({ variables: { value: '' }, client }).data?.allBranches
  const [_, msgr] = useMsgs()
  const [logExternalOrder, { loading: logLoading }] = useLogExternalOrderMutation({
    client,
    onError: (error) => {
      if (error.message) msgr.add(error.message, 'negative')
      else msgr.addUnknownError()
    },
  })
  const [updateExternalOrder, { loading: updateLoading }] = useUpdateExternalOrderMutation({
    client,
    onError: (error) => {
      if (error.message) msgr.add(error.message, 'negative')
      else msgr.addUnknownError()
    },
  })
  const [vendorModalOpen, vendorModalToggle] = useToggle()
  const [machineModalOpen, machineModalToggle] = useToggle()
  const [locationModalOpen, locationModalToggle] = useToggle()
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false)
  const [errors, setErrors] = useState<ExternalOrderError>()
  const [fields, setFields] = useState<Fields>(initialValues || newFields)
  // True while ClueWorkOrderNumberInput is still resolving the typed WO
  // number — submit must wait for the verdict, not race it.
  const [clueWoChecking, setClueWoChecking] = useState(false)

  const [fleetioSelectedLocationId, setFleetioSelectedLocationId] = useState<number | undefined>(
    undefined
  )

  const editing = !!storeOrderId

  // Clue-integrated orgs must attach every logged order to a Clue work order
  // — the write-back needs Clue's idWo linkage — but the machine itself is no
  // longer forced (GEA-5065): "no machine involved" is back for Clue orgs,
  // and on that path the typed WO number resolves against Clue instead
  // (matches the server-side rule in Gf.Buyers.Forms.LogExternalOrder).
  const machineInvolved = fields.machineInvolved

  // Mirrors the backend's generates_purchase_order?/1 — the PO is system-owned
  // for these orgs (the Clue integration), so the form never lets a user value win.
  const generatesPurchaseOrder =
    organization.showPurchaseOrder && organization.generatePurchaseOrderNumber

  // On create the real PO is generated server-side and unknown to the client, so any
  // value sitting in fields.purchaseOrderNumber (e.g. an OCR'd doc PO) is stale and
  // must not be shown or submitted. On edit, fields.purchaseOrderNumber is the saved
  // system PO and is authoritative.
  const purchaseOrderIsAutoGeneratedOnCreate = generatesPurchaseOrder && !editing

  const updateFields = useCallback(
    (updates: Partial<Fields>) => setFields((prev) => ({ ...prev, ...updates })),
    []
  )

  // Location groups: fetch, sort (user groups first), and auto-select
  const { data: locationGroupsData } = useExternalOrderLocationGroupsQuery({
    variables: { orgId, userId: user.id },
    client,
    skip: !orgId || !user?.id,
  })
  const userLocationGroups = useMemo(
    () =>
      (locationGroupsData?.user?.shippingLocationGroups ?? []).filter(
        (g): g is NonNullable<typeof g> => Boolean(g)
      ),
    [locationGroupsData]
  )
  const sortedLocationGroups = useMemo(() => {
    const orgGroups = (locationGroupsData?.org?.shippingLocationGroups ?? []).filter(
      (g): g is NonNullable<typeof g> => Boolean(g)
    )
    const userGroupIds = new Set(userLocationGroups.map((g) => g.id))
    return [...userLocationGroups, ...orgGroups.filter((g) => !userGroupIds.has(g.id))]
  }, [locationGroupsData, userLocationGroups])

  useEffect(() => {
    if (!editing && sortedLocationGroups.length > 0 && !fields.locationGroupId) {
      updateFields({ locationGroupId: sortedLocationGroups[0].id })
    }
  }, [editing, sortedLocationGroups, fields.locationGroupId, updateFields])

  const updateItemField = (index: number, updates: Partial<Item>) => {
    const items = Object.assign([], fields.items, {
      [index]: { ...fields.items[index], ...updates },
    })

    updateFields({ items })
  }

  const { data: selectedMachineData } = useSelectedMachineQuery({
    variables: { id: fields.orgMachineId ?? '' },
    client,
    skip: !fields.orgMachineId,
    fetchPolicy: 'cache-first',
  })

  // GEA-9808: a logged order opens its M5 part requests like any Gearflow-born request, and M5
  // needs a job code on each one. Same rule as create, Add part and Order manually: the picker
  // shows when the work order comes from M5 (WorkOrderCombobox below) and the auto-open is on,
  // and a code is required only when the work order has job lines (none is the GEA-5927 case).
  const jobCodeWorkOrderId =
    (!features.hasClueIntegration &&
      !!featureFlags.assetworksApiIssuePartsToWorkOrder &&
      !!featureFlags.assetworksAutoOpenPartRequests &&
      fields.workOrderNumber?.trim()) ||
    ''

  const { data: workOrderTasksData, loading: workOrderTasksLoading } = useGetWorkOrderTasksQuery({
    client,
    variables: { workOrderId: jobCodeWorkOrderId },
    skip: !jobCodeWorkOrderId,
  })

  const workOrderTasks = workOrderTasksData?.getWorkOrderTasks ?? []
  const jobCodeRequired = !!jobCodeWorkOrderId && workOrderTasks.length > 0
  // Until the job lines arrive the form cannot know whether a code is required, so hold the
  // submit: a line sent in that window opens under the work order's first open job.
  const jobCodesLoading = !!jobCodeWorkOrderId && workOrderTasksLoading
  const [jobCodeErrorsShown, setJobCodeErrorsShown] = useState(false)

  // Start each line without a code on the first job line, the one the M5 open would pick.
  const defaultJobCode = jobCodeWorkOrderId ? workOrderTasks[0]?.taskId : undefined
  const itemNeedsDefaultJobCode = fields.items.some((i) => !i.taskNumber)

  useEffect(() => {
    if (!defaultJobCode || !itemNeedsDefaultJobCode) return

    setFields((prev) => ({
      ...prev,
      items: prev.items.map((i) => (i.taskNumber ? i : { ...i, taskNumber: defaultJobCode })),
    }))
  }, [defaultJobCode, itemNeedsDefaultJobCode])

  const { uploadProcessing, uploadStarted, resetUpload, extractOrderDoc } = useUpload(
    (orderDoc, documentId) => {
      setErrors(undefined)

      const items = orderDoc.items!.map(({ properties }) => {
        const unitPrice = properties.find((p) => p.__typename === 'OrderDocItemUnitPrice')?.price
        const totalPrice = properties.find((p) => p.__typename === 'OrderDocItemTotalPrice')?.price
        const quantity = properties.find((p) => p.__typename === 'OrderDocItemQuantity')?.quantity

        return {
          id: null,
          partNumber: properties.find((p) => p.__typename === 'OrderDocItemPartNumber')?.text || '',
          description:
            properties.find((p) => p.__typename === 'OrderDocItemDescription')?.text || '',
          unitPrice: unitPrice || totalPrice || null,
          quantity: (unitPrice && quantity) || 1,
          jobQuantities: [],
          costCode: null,
          phaseCode: null,
          taskNumber: null,
        }
      })

      updateFields({
        documentId,
        purchaseOrderNumber:
          orderDoc.properties.find((p) => p.__typename === 'OrderDocPoNumber')?.text || '',
        taxCost: orderDoc.properties.find((p) => p.__typename === 'OrderDocTax')?.price,
        shippingCost: orderDoc.properties.find((p) => p.__typename === 'OrderDocShippingCost')
          ?.price,
        items: items.length > 0 ? items : [newItem],
      })
    }
  )

  const { totals } = useQuoteTotals({
    items: fields.items.map((i) => ({
      ...i,
      unitPrice: i.unitPrice ? MoneyM.toDecimal(i.unitPrice) : null,
    })),
    taxAmount: fields.taxCost,
    shippingAmount: fields.shippingCost,
    customerDiscount: 0,
    customerFeeRate: null,
  })

  const onSubmit = async () => {
    setConfirmationOpen(false)

    if (jobCodesLoading) {
      msgr.add('Still loading the job lines from M5 — one moment', 'negative')
      return
    }

    if (jobCodeRequired && fields.items.some((i) => !i.taskNumber?.trim())) {
      setJobCodeErrorsShown(true)
      msgr.add('Select a job code for every part', 'negative')
      return
    }

    // Clue-integrated orgs: a Clue work order is always required — the WO
    // drives our write-back to Clue — independent of the org's
    // createRequestRequireWorkOrder setting. With a machine involved the
    // picker supplies it; with "no machine involved" the typed number must
    // have resolved to an open Clue WO (GEA-5065).
    if (features.hasClueIntegration && machineInvolved && !fields.orgMachineId) {
      msgr.add('Machine is required', 'negative')
      return
    }

    if (features.hasClueIntegration && machineInvolved && !fields.externalWorkOrderId) {
      msgr.add('Clue work order is required', 'negative')
      return
    }

    if (features.hasClueIntegration && !machineInvolved) {
      if (!fields.workOrderNumber?.trim()) {
        msgr.add('Work order number is required', 'negative')
        return
      }

      if (clueWoChecking) {
        msgr.add('Still verifying the work order number with Clue — one moment', 'negative')
        return
      }

      if (!fields.externalWorkOrderId) {
        msgr.add('No open Clue work order found with this number', 'negative')
        return
      }
    }

    // Validate Work Order number if required by organization and machine is involved
    if (
      organization.createRequestRequireWorkOrder &&
      machineInvolved &&
      !selectedMachineData?.orgMachine?.fleetioId &&
      !fields.externalWorkOrderId &&
      !fields.workOrderNumber?.trim()
    ) {
      msgr.add('Work order number is required', 'negative')
      return
    }

    const variables = {
      vendorId: fields.vendorId,
      // System-owned on create — let the server generate it; never submit a stale client value.
      purchaseOrderNumber: purchaseOrderIsAutoGeneratedOnCreate ? null : fields.purchaseOrderNumber,
      workOrderNumber: fields.workOrderNumber,
      shippingCost: fields.shippingCost,
      taxCost: fields.taxCost,
      notes: fields.notes,
      orgMachineId: machineInvolved ? fields.orgMachineId : null,
      branchId: fields.branchId,
      locationId: fields.locationId,
      locationGroupId: fields.locationGroupId,
      documentUrl: fields.documentUrl,
      documentId: fields.documentId,
      fleetioWorkOrderId: fields.fleetioWorkOrderId,
      externalWorkOrderId: fields.externalWorkOrderId,
      fleetioLocationId: fleetioSelectedLocationId ?? null,
      items: fields.items.map(({ jobQuantities, costCode, phaseCode, taskNumber, ...rest }) => ({
        ...rest,
        // A job code only means something on an M5 work order the picker loaded.
        taskNumber: jobCodeWorkOrderId ? (taskNumber?.trim() ?? null) || null : null,
        jobQuantities: jobQuantities.map(({ jobId, quantity }) => ({ jobId, quantity })),
        costCodeId: costCode?.id ?? null,
        phaseCodeId: phaseCode?.id ?? null,
        // Server treats nil here as an improper list constructor (`[mpn | nil]`)
        // and crashes — send an empty list when no alt identifiers are set.
        altPartNumbers: [],
      })),
    }

    if (storeOrderId) {
      const { data } = await updateExternalOrder({
        variables: {
          ...variables,
          storeOrderId,
        },
      })

      if (data!.updateExternalOrder.__typename === 'ExternalOrderOk') {
        navigate(`/orders/${data!.updateExternalOrder.storeOrderId}`)
      } else {
        setErrors(data!.updateExternalOrder)
        msgr.add('Some fields are invalid, please check the form', 'negative')
      }
    } else {
      const { data } = await logExternalOrder({
        variables: { ...variables, documentUrl: fields.documentUrl ?? null },
      })

      if (data!.logExternalOrder.__typename === 'ExternalOrderOk') {
        navigate(`/orders/${data!.logExternalOrder.storeOrderId}`)
      } else {
        setErrors(data!.logExternalOrder)
        if (data?.logExternalOrder?.message) msgr.add(data.logExternalOrder.message, 'negative')
        else msgr.add('Some fields are invalid, please check the form', 'negative')
      }
    }
  }

  // The form keeps the uploaded object's own URL — that's what gets extracted and submitted.
  // Only the preview reads through the authed proxy (GEA-4585), which is what still works
  // once the uploads bucket goes private. Null with the proxy off: render the object itself.
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState<string | null>(null)

  const onDocumentUploadComplete = (documentUrl: string, previewUrl: string | null) => {
    updateFields({ documentUrl })
    setDocumentPreviewUrl(previewUrl)
    extractOrderDoc(documentUrl)
  }

  return (
    <>
      <Modal open={vendorModalOpen} onClose={vendorModalToggle.off}>
        <AddVendorModal
          draftMode={true}
          onClose={vendorModalToggle.off}
          onVendorCreated={(vendorId) => {
            updateFields({ vendorId })
          }}
        />
      </Modal>

      <AddAccountMachineModal
        open={machineModalOpen}
        onClose={machineModalToggle.off}
        onAdd={(om) => {
          if (om) updateFields({ orgMachineId: om.id })
        }}
        accountId={organization.id}
      />

      <LocationModal
        open={locationModalOpen}
        onClose={locationModalToggle.off}
        onComplete={(locationId) => updateFields({ locationId })}
        branches={(organization.requireBillingCompany ? branches : []) ?? []}
        buyers={[]}
        initialAddress={Address.init()}
        refetchQueries={[LocationsQuery]}
        showPersist
      />

      {confirmationOpen && (
        <AlertDialog key="confirmation-dlg" open>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Order Submission</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>Please review the order details before submitting:</p>
                <ul className="list-disc pl-4">
                  <li>Total parts: {fields.items.length}</li>
                  <li>Total amount: {MoneyM.format(totals.total)}</li>
                  {fields.purchaseOrderNumber && !purchaseOrderIsAutoGeneratedOnCreate && (
                    <li>PO Number: {fields.purchaseOrderNumber}</li>
                  )}
                  {fields.workOrderNumber ? (
                    <li>Work Order: {fields.workOrderNumber}</li>
                  ) : fields.externalWorkOrderId ? (
                    <li>Work Order: {fields.externalWorkOrderId}</li>
                  ) : null}
                </ul>
                <p>Are you sure you want to proceed with logging this order?</p>
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmationOpen(false)}>
                Cancel
              </AlertDialogCancel>
              <Action.P
                onClick={onSubmit}
                disabled={logLoading || updateLoading || jobCodesLoading}
              >
                Submit Order
              </Action.P>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {resetConfirmationOpen && (
        <AlertDialog key="reset-confirmation-dlg" open>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Form Reset</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to reset the form? All unsaved changes will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setResetConfirmationOpen(false)}>
                Cancel
              </AlertDialogCancel>
              <Action.P
                onClick={() => {
                  setFields(initialValues ?? newFields)
                  setResetConfirmationOpen(false)
                }}
              >
                Reset Form
              </Action.P>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <Form
        onSubmit={(e) => {
          e.preventDefault()
          setConfirmationOpen(true)
        }}
        className={`flex gap-x-6 gap-y-4 mt-4 pb-32 ${fields.documentUrl || uploadProcessing ? 'flex-col lg:flex-row max-w-full' : 'flex-wrap md:flex-nowrap max-w-screen-xl'}`}
      >
        <Box
          className={`space-y-8 text-sm p-6 flex-shrink-0 ${fields.documentUrl || uploadProcessing ? 'lg:max-w-[32rem]' : ''}`}
        >
          <div className="space-y-4">
            <div className="text-xl">Provide Order Details</div>

            <Field label="Vendor" errors={errors?.vendorId}>
              <div className="flex items-center gap-x-4">
                <VendorsSelector
                  value={fields.vendorId}
                  onChange={(vendorId) => updateFields({ vendorId })}
                />

                {!features?.vendorCreationDisabled && (
                  <Action.T onClick={vendorModalToggle.on}>Add vendor</Action.T>
                )}
              </div>
            </Field>

            {organization.requireBillingCompany && (
              <Field label="What billing company?" errors={errors?.branchId}>
                <BranchSelector
                  className="max-w-xs"
                  value={fields.branchId}
                  onChange={(branchId) => updateFields({ branchId })}
                />
              </Field>
            )}

            {sortedLocationGroups.length > 0 && (
              <Field label="Location Group">
                <select
                  className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={fields.locationGroupId ?? ''}
                  onChange={(e) => {
                    const groupId = e.target.value || null
                    updateFields({ locationGroupId: groupId })
                  }}
                >
                  {sortedLocationGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                      {userLocationGroups.some((ug) => ug.id === group.id) ? ' (Your Group)' : ''}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Location" errors={errors?.locationId} className="">
              <div className="flex items-center gap-x-4">
                <LocationSelector
                  value={fields.locationId}
                  onChange={(locationId) => updateFields({ locationId })}
                />

                <Action.T onClick={locationModalToggle.on}>Add location</Action.T>
              </div>
            </Field>

            <Field label="Machine">
              <Checkbox
                label="No machine involved"
                checked={!fields.machineInvolved}
                onChange={({ target }) =>
                  // Toggling swaps the WO control (machine-scoped picker vs
                  // typed-number resolution) — drop any value picked/typed
                  // against the previous one.
                  updateFields({
                    machineInvolved: !target.checked,
                    orgMachineId: null,
                    workOrderNumber: '',
                    fleetioWorkOrderId: null,
                    externalWorkOrderId: null,
                  })
                }
              />

              {machineInvolved && (
                <div className="flex items-center gap-x-4">
                  <div className="w-full max-w-xs">
                    <MachineSelect
                      value={fields.orgMachineId}
                      onChange={(orgMachineId) =>
                        updateFields({
                          orgMachineId,
                          fleetioWorkOrderId: null,
                          externalWorkOrderId: null,
                        })
                      }
                    />
                  </div>

                  <Action.T onClick={machineModalToggle.on}>Add machine</Action.T>
                </div>
              )}
            </Field>
          </div>

          <hr />

          {fields.items.map((item, index) => (
            <div className="space-y-3" key={index}>
              <div className="flex justify-between">
                <h4 className="text-lg font-medium">
                  Part {fields.items.length > 1 ? index + 1 : null}
                </h4>

                {index > 0 && (
                  <Action.T
                    onClick={() =>
                      updateFields({ items: fields.items.filter((_, i) => i !== index) })
                    }
                  >
                    Remove
                  </Action.T>
                )}
              </div>

              <div className="flex gap-x-2">
                <Field
                  label="Part Number"
                  errors={errors?.items?.at(index)?.partNumber}
                  className="w-1/3"
                >
                  <TextInput
                    value={item.partNumber}
                    setValue={(partNumber) => updateItemField(index, { partNumber })}
                    placeholder="ie. ES3897B"
                  />
                </Field>

                <Field
                  label="Quantity"
                  errors={errors?.items?.at(index)?.quantity}
                  className="w-1/3"
                >
                  <QuantityInput
                    value={item.quantity}
                    setValue={(quantity) => updateItemField(index, { quantity })}
                  />
                </Field>

                <Field
                  label="Unit Price"
                  errors={errors?.items?.at(index)?.unitPrice}
                  className="w-1/3"
                >
                  <PriceV2
                    price={item.unitPrice}
                    onChange={(unitPrice) => updateItemField(index, { unitPrice })}
                  />
                </Field>
              </div>

              <Field label="Part Description" errors={errors?.items?.at(index)?.description}>
                <TextInput
                  value={item.description}
                  setValue={(description) => updateItemField(index, { description })}
                  placeholder="ie. Air Filter, Fuel Pump"
                />
              </Field>

              {jobCodeWorkOrderId && (
                <Field
                  label="Job Code"
                  errors={
                    jobCodeErrorsShown && jobCodeRequired && !item.taskNumber
                      ? 'Select a job code'
                      : undefined
                  }
                >
                  <div className="w-48">
                    <JobCodeCombobox
                      jobCode={item.taskNumber ?? undefined}
                      onJobCodeChange={(taskNumber) => updateItemField(index, { taskNumber })}
                      workOrderId={jobCodeWorkOrderId}
                    />
                  </div>
                </Field>
              )}

              {organization.costCodesEnabled &&
                (item.costCode ? (
                  <div className="flex gap-x-4">
                    <Field
                      label="Cost Code"
                      errors={errors?.items?.[index]?.description}
                      htmlFor={`costCode`}
                      className="flex-grow"
                    >
                      <CostCodeSelector
                        autoFocus
                        value={item.costCode?.id ?? null}
                        onChange={(costCodeId) =>
                          updateItemField(index, { costCode: { id: costCodeId } })
                        }
                      />
                    </Field>

                    <Action.T
                      className="mt-[26px] h-8 text-sm"
                      onClick={() => updateItemField(index, { costCode: null })}
                    >
                      Remove
                    </Action.T>
                  </div>
                ) : (
                  <Action.S
                    onClick={() => updateItemField(index, { costCode: { id: null } })}
                    size="sm"
                    className="flex gap-x-1.5"
                  >
                    <Plus className="h-4 w-4" /> Add Cost Code
                  </Action.S>
                ))}

              {item.jobQuantities.map((jq, jIndex) => (
                <div key={jq.id} className="flex gap-x-4">
                  <Field
                    label="Job Number"
                    errors={errors?.items?.[index]?.jobQuantities?.[jIndex]?.jobId}
                    htmlFor={`jobNumber${jIndex}`}
                    className="flex-grow"
                  >
                    <JobSelector
                      value={jq.jobId}
                      onChange={(jobId) =>
                        updateItemField(index, {
                          jobQuantities: item.jobQuantities.map((jq, i) =>
                            i === jIndex ? { ...jq, jobId } : jq
                          ),
                        })
                      }
                    />
                  </Field>

                  <Field
                    label="Quantity"
                    errors={errors?.items?.[index]?.jobQuantities?.[jIndex]?.quantity}
                    className="w-16 flex-shrink-0"
                  >
                    <TextInput
                      type="number"
                      min="1"
                      value={jq.quantity?.toString() ?? ''}
                      onChange={(e) => {
                        const jobQuantity = parseInt(e.target.value, 10)

                        let newItemQuantity = item.quantity
                        if (Number.isInteger(jobQuantity)) {
                          newItemQuantity = item.jobQuantities.reduce((sum, jq, currIndex) => {
                            return (
                              sum +
                              (index === currIndex
                                ? jobQuantity
                                : jq.quantity
                                  ? parseInt(jq.quantity + '', 10)
                                  : 0)
                            )
                          }, 0)
                        }

                        updateItemField(index, {
                          jobQuantities: item.jobQuantities.map((jq, i) =>
                            i === jIndex ? { ...jq, quantity: e.target.valueAsNumber } : jq
                          ),
                          quantity: newItemQuantity,
                        })
                      }}
                    />
                  </Field>
                  <Action.T
                    className="mt-6 h-8 text-sm"
                    onClick={() => {
                      const newItemQuantity = item.jobQuantities.reduce(
                        (sum, jq, currIndex) =>
                          sum +
                          (index === currIndex
                            ? 0
                            : jq.quantity
                              ? parseInt(jq.quantity + '', 10)
                              : 0),
                        0
                      )

                      updateItemField(index, {
                        quantity: newItemQuantity,
                        jobQuantities: item.jobQuantities.filter((_m, j) => j !== jIndex),
                      })
                    }}
                  >
                    Remove
                  </Action.T>
                </div>
              ))}

              {organization.requestsJobNumbersEnabled && (
                <div>
                  <Action.S
                    onClick={() => {
                      const newItemQuantity = item.jobQuantities.reduce(
                        (sum, jq) => sum + jq.quantity,
                        0
                      )

                      updateItemField(index, {
                        quantity: newItemQuantity + 1,
                        jobQuantities: [
                          ...item.jobQuantities,
                          { id: uniqueId(), jobId: '', quantity: 1 },
                        ],
                      })
                    }}
                    size="sm"
                    className="flex gap-x-1.5"
                  >
                    <Plus className="h-4 w-4" /> Add Job Number
                  </Action.S>
                </div>
              )}
            </div>
          ))}

          <hr />

          <div>
            <Action.S
              onClick={() => updateFields({ items: [...fields.items, newItem] })}
              className="flex gap-1 items-center"
            >
              <Plus className="h-5 w-5" /> Add part
            </Action.S>
          </div>

          <div className="space-y-4">
            {selectedMachineData?.orgMachine?.fleetioId ? (
              <></>
            ) : (
              <Field
                label={`PO number${!organization.generatePurchaseOrderNumber ? ' (optional)' : ''}`}
              >
                {generatesPurchaseOrder ? (
                  <div className="text-sm block space-y-2">
                    <p>
                      {/* On edit, show the order's saved system-owned PO; on create,
                          preview the next number that will be generated. Read-only either way. */}
                      {editing ? (
                        fields.purchaseOrderNumber
                      ) : (
                        <NextPurchaseOrderNumber orgMachineId={fields.orgMachineId} />
                      )}{' '}
                      <span className="text-gray-500 italic">(Auto-generated)</span>
                    </p>
                  </div>
                ) : (
                  <TextInput
                    value={fields.purchaseOrderNumber}
                    setValue={(purchaseOrderNumber) => updateFields({ purchaseOrderNumber })}
                  />
                )}
              </Field>
            )}

            <Field
              label={`Work Order number${features.hasClueIntegration || (organization.createRequestRequireWorkOrder && machineInvolved) ? '' : ' (optional)'}`}
            >
              {features.hasClueIntegration && !machineInvolved ? (
                // "No machine involved" on a Clue org (GEA-5065): no machine
                // to list WOs for, so the buyer types the WO number and it
                // resolves against Clue. The resolved Clue id lands in
                // externalWorkOrderId (null until confirmed — submit
                // validation above blocks without it).
                <ClueWorkOrderNumberInput
                  value={fields.workOrderNumber}
                  onChange={({ number, id, checking }) => {
                    setClueWoChecking(checking)
                    updateFields({
                      workOrderNumber: number,
                      externalWorkOrderId: id,
                      fleetioWorkOrderId: null,
                    })
                  }}
                />
              ) : features.hasClueIntegration && !fields.orgMachineId ? (
                // The Clue WO picker needs a machine to query against, and a
                // free-text WO would be rejected server-side anyway — hold the
                // field until the machine is picked.
                <p className="text-sm italic text-gray-500">
                  Select a machine to choose its Clue work order.
                </p>
              ) : features.hasClueIntegration && fields.orgMachineId ? (
                <ClueWorkOrderNumber
                  workOrderId={fields.externalWorkOrderId ?? undefined}
                  workOrderNumber={fields.workOrderNumber}
                  orgMachineId={fields.orgMachineId}
                  onChange={({ id, number }) =>
                    // Keep the Clue id for write-back, and persist the human WO
                    // number so order/RFQ views (which render work_order_number)
                    // show the value mechanics recognise instead of an empty cell.
                    // The three sources (Clue / Fleetio / free-text) are
                    // mutually exclusive — clear any stale Fleetio id carried
                    // over from a prior machine pick so the mutation doesn't
                    // submit both at once.
                    updateFields({
                      externalWorkOrderId: id,
                      workOrderNumber: number,
                      fleetioWorkOrderId: null,
                    })
                  }
                />
              ) : featureFlags.assetworksApiIssuePartsToWorkOrder ? (
                <WorkOrderCombobox
                  workOrder={fields.workOrderNumber}
                  onWorkOrderChange={(workOrderNumber) =>
                    // The job codes belong to the old work order's job lines, so clear them.
                    setFields((prev) => ({
                      ...prev,
                      workOrderNumber,
                      externalWorkOrderId: null,
                      items:
                        workOrderNumber === prev.workOrderNumber
                          ? prev.items
                          : prev.items.map((i) => ({ ...i, taskNumber: null })),
                    }))
                  }
                  unitNumber={selectedMachineData?.orgMachine?.name ?? undefined}
                />
              ) : fields.orgMachineId && selectedMachineData?.orgMachine?.fleetioId ? (
                <FleetioWorkOrderNumber
                  workOrderId={fields.fleetioWorkOrderId ?? undefined}
                  orgMachineId={fields.orgMachineId ?? null}
                  onChange={(value) =>
                    updateFields({ fleetioWorkOrderId: value, externalWorkOrderId: null })
                  }
                />
              ) : (
                <TextInput
                  value={fields.workOrderNumber}
                  setValue={(workOrderNumber) =>
                    updateFields({ workOrderNumber, externalWorkOrderId: null })
                  }
                />
              )}
            </Field>

            {fields.orgMachineId && selectedMachineData?.orgMachine?.fleetioId && !editing ? (
              <>
                <Field>
                  <FleetioLocationSelector
                    initialSelection={null}
                    selectedId={fleetioSelectedLocationId}
                    onChange={(id) => setFleetioSelectedLocationId(id)}
                  />
                </Field>
              </>
            ) : (
              <></>
            )}

            <Field label="Notes (optional)">
              <TextArea
                value={fields.notes ?? ''}
                onChange={(e) => updateFields({ notes: e.target.value })}
                rows={3}
              />
            </Field>

            <Field label="Shipping cost (optional)">
              <PriceV2
                price={fields.shippingCost}
                onChange={(shippingCost) => updateFields({ shippingCost })}
              />
            </Field>

            <Field label="Sales tax (optional)">
              <PriceV2 price={fields.taxCost} onChange={(taxCost) => updateFields({ taxCost })} />
            </Field>

            <Summary
              total={totals.total}
              subtotal={totals.subtotal}
              discount={totals.discount}
              discountPercent={null}
              shippingCost={fields.shippingCost}
              taxCost={fields.taxCost ?? zeroPrice}
              customerFee={totals.customerFee}
              refunded={MoneyM.fromInt(0, 'USD')}
              showShippingCost
            />
          </div>
        </Box>

        <div className="relative flex-1">
          <div className="w-full flex flex-col gap-y-4 sticky top-4">
            {!editing && (
              <Box className="shadow-base p-6 space-y-4">
                {uploadProcessing ? (
                  <>
                    <div className="text-lg font-medium">Processing Document</div>
                    <div className="border border-gray-200 rounded-lg p-8 flex flex-col items-center justify-center space-y-4">
                      <LoaderCircle className="animate-spin h-8 w-8 text-blue-500" />
                      <div className="text-center space-y-2">
                        <p className="font-medium">Processing file</p>
                        <p className="text-sm text-gray-600">
                          Most documents finish in 10–30 seconds. Long or scanned PDFs can take a
                          couple of minutes — please don't navigate away.
                        </p>
                      </div>
                      <Action.S onClick={resetUpload} className="mt-4">
                        Cancel
                      </Action.S>
                    </div>
                  </>
                ) : fields.documentUrl ? (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-gray-100 px-2 py-1 rounded-md max-w-full gap-2 border border-gray-200">
                        <span className="text-sm font-medium text-gray-700 flex-1 break-all">
                          {fields.documentUrl.split('/').pop() || ''}
                        </span>
                        <button
                          onClick={() => {
                            updateFields({ documentUrl: null, documentId: null })
                            setDocumentPreviewUrl(null)
                          }}
                          className="text-gray-500 hover:text-gray-700 focus:outline-none flex-shrink-0"
                          aria-label="Remove PDF"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                      {fields.documentUrl.toLowerCase().endsWith('.pdf') ? (
                        <iframe
                          src={documentPreviewUrl ?? fields.documentUrl}
                          className="w-full h-[600px] border border-gray-200 rounded-md"
                          title="PDF Viewer"
                        />
                      ) : (
                        <ImageViewer
                          src={documentPreviewUrl ?? fields.documentUrl}
                          alt="Document preview"
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <Upload onStarted={uploadStarted} onComplete={onDocumentUploadComplete} />
                )}
              </Box>
            )}
          </div>
        </div>
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
          <div className="flex gap-4 justify-end">
            <Action.S onClick={() => setResetConfirmationOpen(true)}>Reset Form</Action.S>
            <Action.P type="submit" disabled={logLoading || updateLoading || jobCodesLoading}>
              {editing ? 'Save' : 'Log Order'}
            </Action.P>
          </div>
        </div>
      </Form>
    </>
  )
}

export default ExternalOrderForm
